<template>
    <div class="u-height-100 u-width-100 d-flex">
        <component :is="layout">
            <router-view v-slot="{ Component }">
                <keep-alive :max="4">
                    <component :is="Component" />
                </keep-alive>
            </router-view>
            <v-overlay
                :model-value="loadingOverlay"
                class="align-center justify-center"
                scrim="white"
                z-index="5002">
                <DataLoading size="40" />
            </v-overlay>
            <ScrollToTopButton />
        </component>
        <ModalsContainer />
        <NotificationContainer />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { DataLoading } from '@components/loading';
import NotificationContainer from '@components/notifications/NotificationContainer.vue';
import ScrollToTopButton from '@components/ScrollToTopButton.vue';

export default {
    name: 'App',
    components: {
        DataLoading,
        ScrollToTopButton,
        NotificationContainer,
    },
    computed: {
        ...mapGetters({
            user: 'oidcStore/oidcUser',
            userIsAuth: 'oidcStore/oidcIsAuthenticated',
            userToken: 'oidcStore/oidcAccessToken',
        }),
        ...mapState({
            loadingOverlay: (state) => state['appState'].loading,
        }),
        layout() {
            const layout = this.$route.meta.layout;

            if (!layout || layout.toLocaleLowerCase() === 'loading') {
                return 'loading-layout';
            }

            return 'default-layout';
        },
    },
    methods: {
        ...mapActions({
            oidcAuth: 'oidcStore/authenticateOidc',
            userGetPartners: 'user/partners/get',
            userTokenDecode: 'user/tokenDecode',
            userSetLoaded: 'user/setLoaded',
            notifyError: 'notification/error',
        }),
        showModal(to) {
            let meta = to.meta;
            let modalConfig = meta.modalConfig;
            const componentProps = Object.assign({}, modalConfig.props, to.params);

            if (modalConfig.showDynamic) {
                this.$modal.showDynamic(
                    modalConfig.component,
                    {
                        desktop: componentProps,
                        mobile: componentProps,
                    },
                    {
                        desktop: modalConfig.configDesktop,
                        mobile: modalConfig.configMobile,
                    }
                );

                return;
            }

            const modalPropsOverwrite = {
                ...(this.$vuetify.display.mdAndUp
                    ? modalConfig.configDesktop
                    : modalConfig.configMobile),
            };
            const showBottom = !!modalPropsOverwrite.showBottom;
            delete modalPropsOverwrite.showBottom;
            const modalProps = Object.assign(
                {
                    'no-click-animation': true,
                    persistent: true,
                    scrollable: true,
                    'hide-overlay': true,
                    'retain-focus': false,
                },
                modalConfig.config,
                modalPropsOverwrite
            );

            if (showBottom) {
                this.$modal.showBottom(modalConfig.component, componentProps, modalProps);
                return;
            }

            this.$modal.show(modalConfig.component, componentProps, modalProps);
        },
        applyBeforeEachOnRouter() {
            const router = this.$router;
            const filterEachRoute = async (to, from, next) => {
                if (to.meta.type === 'modal') {
                    this.showModal(to);
                    next(from);
                } else {
                    next();
                }
            };

            router.beforeEach(filterEachRoute);
        },

        onUserLoaded() {
            setTimeout(() => {
                this.userTokenDecode(this.userToken);
                this.userSetLoaded(true);

                if (this.user.partner_ids) {
                    this.userGetPartners({
                        refresh: true,
                        offset: 0,
                        limit: 99,
                        filter: { partnerIds: this.user.partner_ids },
                    });
                } else {
                    this.notifyError({
                        content: this.$t('_common:messages.missingPartners'),
                    });
                }
            }, 100);
        },
    },
    created() {
        window.addEventListener('vuexoidc:userLoaded', this.onUserLoaded, { passive: true });
        if (!this.userIsAuth && this.$route.query.session_state) {
            this.oidcAuth();
        }

        this.$router.isReady().then(async () => {
            this.applyBeforeEachOnRouter();
        });
    },
    unmounted() {
        window.removeEventListener('vuexoidc:userLoaded', this.onUserLoaded, { passive: true });
    },
};
</script>
